import React, { useEffect, useMemo, useState } from 'react';
import { isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { LeadConfigModel } from '../../../shared/models/lead.model';
import { OpsDateBasis, OpsFilterModel } from '../../../shared/models/lead-ops.model';
import { getLeadConfig } from '../../../shared/services/data/lead.data';
import { CHIP } from '../../shared/lead-visuals';
import { OpsFilterBar } from './components/ops-filter-bar.component';
import { StatusAndOverviewSection } from './sections/status-and-overview.section';
import { TeamAndAgentsSection } from './sections/team-and-agents.section';
import { LostAnalysisSection, PendingPipelineSection } from './sections/pipeline-and-lost.section';
import { SlaAttemptsSection, VerticalFunnelSection } from './sections/funnel-and-sla.section';

const isoDaysAgo = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
};

/**
 * Leads Ops.
 *
 * Nine sections, each loading its own report. That is deliberate: a slow or
 * broken aggregation shows an error in its own card and can be retried there,
 * while everything else stays readable. Behind one request the dashboard would
 * only be as available as its worst query.
 *
 * Every number here is scoped to the caller by the lead service. A manager
 * sees their subtree, an agent sees their own book — the page does not say so
 * on every card, but nothing on it is org-wide unless the reader's access is.
 */
export const LeadsOpsContainer: React.FC = () => {
  const [config, setConfig] = useState<LeadConfigModel | undefined>();
  const [filter, setFilter] = useState<OpsFilterModel>({
    // Last 30 days by arrival, which is the question most people open this to
    // ask. Anything longer makes the first paint slow for no one's benefit.
    dateFrom: isoDaysAgo(30),
    dateTo: new Date().toISOString().slice(0, 10),
    dateBasis: OpsDateBasis.CREATED
  });

  useEffect(() => {
    getLeadConfig().then((result) => {
      if (!isErrorInfo(result)) setConfig(result);
    });
  }, []);

  const onChange = (patch: Partial<OpsFilterModel>) =>
    setFilter((current) => ({ ...current, ...patch }));

  // Empty arrays are dropped rather than sent: the service reads an empty
  // array as a filter matching nothing, and every section would go blank the
  // moment somebody cleared a picker.
  const applied: OpsFilterModel = useMemo(() => {
    const next: OpsFilterModel = { ...filter };
    (
      ['vertical', 'status', 'valueCategory', 'assignedTo', 'marketingChannel', 'source'] as const
    ).forEach((key) => {
      if (!next[key]?.length) delete next[key];
    });
    return next;
  }, [filter]);

  return (
    <div className="flex h-full flex-col gap-5 md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[3rem]">
      <section className="flex flex-wrap items-center gap-3">
        <span className="h-8 w-1.5 rounded-full bg-primary-500" aria-hidden="true" />
        <h1 className="font-gilroy-bold text-2xl text-neutral-500">Leads Ops</h1>
        <span className={`${CHIP} bg-creams-400 text-creams-900`}>
          {filter.dateFrom} → {filter.dateTo}
        </span>
      </section>

      <OpsFilterBar filter={filter} config={config} onChange={onChange} />

      <StatusAndOverviewSection filter={applied} />
      <VerticalFunnelSection filter={applied} />
      <TeamAndAgentsSection filter={applied} />
      <SlaAttemptsSection filter={applied} />
      <PendingPipelineSection filter={applied} />
      <LostAnalysisSection filter={applied} />
    </div>
  );
};
